import { useTranslation } from 'react-i18next'

import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Progress, Trans } from '@/components'
import { useUserStore } from '@/stores/user'
import { mb2GbString, toGbStrig } from '@/utils/file'
import { cn } from '@/utils/style'
interface DriveVolumeSectionProps {
  volumes: Volume[]
  domainInfos: ServerDomainInfo[]
}

export default function DriveVolumeSection({ volumes, domainInfos }: DriveVolumeSectionProps) {
  const { t } = useTranslation()
  const userStore = useUserStore()
  const isExpired = userStore.isOrderExpired

  return (
    <div>
      <h3 className="t-sb text-primary mb-4">
        <Trans i18nKey="space.volumeItemTitleCloudStorage">Data Hub</Trans>
      </h3>

      <div className="flex flex-col gap-y-4">
        {domainInfos.map((domainInfo) => {
          const volume = volumes.find((vol) => vol.domain === domainInfo.domain)
          const effectiveQuota = volume ? (isExpired ? volume.last_quota_gb : volume.quota_gb) : 0
          const usage = volume && effectiveQuota > 0 ? volume.usage_mb / 1024 / effectiveQuota : 0

          return (
            <div
              key={`volume.${domainInfo.domain}`}
              className="w-full p-6 flex rounded-2xl gap-3 items-start border border-gray-300"
            >
              <div className="w-[120px]">
                <div className={cn('text-[20px] leading-7 font-bold')}>
                  {volume && effectiveQuota ? toGbStrig(effectiveQuota) : '-'}
                </div>
                <div className="t-sm text-secondary">
                  {t(`common.region.domain${domainInfo.domain}`)}
                </div>
              </div>

              <div className="w-full">
                {/* Show Progress even if there is no volume, value will be 0% */}
                <div className="py-[10px]">
                  <Progress
                    color={cn({
                      'bg-tertiary': isExpired,
                      'bg-error': !isExpired && usage === 1,
                      'bg-warning': !isExpired && usage >= 0.85 && usage < 1,
                      'bg-btn-secondary': !isExpired && usage < 0.85,
                    })}
                    value={usage}
                  />
                </div>

                {volume && effectiveQuota > 0 && (
                  /* If there is a corresponding volume and available space */
                  <div className="flex gap-x-1 t-sm">
                    <>
                      <Trans
                        i18nKey="space.volumeItemUsage"
                        values={{
                          usage: mb2GbString(volume.usage_mb),
                          quota: toGbStrig(effectiveQuota),
                        }}
                        components={[
                          <span className="t-sm text-secondary">Using </span>,
                          <span className="t-sm text-secondary"> of </span>,
                        ]}
                      >
                        Using {{ usage: mb2GbString(volume.usage_mb) }}GiB of{' '}
                        {{ quota: toGbStrig(effectiveQuota) }}GiB
                      </Trans>

                      <div className="t-sm text-tertiary">
                        &nbsp;&nbsp;{`${(usage * 100).toFixed(2) || '0'}`}%
                      </div>
                    </>
                  </div>
                )}

                {usage >= 0.85 && usage < 1 && (
                  <div className="flex gap-x-1 t-sm text-warning">
                    <Trans i18nKey="space.volumeItemLowStorage">
                      Low storage – manage your storage to avoid interruption
                    </Trans>
                  </div>
                )}

                {(!volume || effectiveQuota <= 0 || usage >= 1) && (
                  /* If there is no corresponding volume or quota is insufficient, show N/A message */
                  <div className={cn('t-sm ', usage >= 1 ? 'text-error' : 'text-tertiary')}>
                    <Trans i18nKey="space.volumeItemNotAvailable">
                      No storage available – adjust your quota to continue
                    </Trans>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
